import type { Message } from "../types"

/**
 * Which messages are unread, derived from the conversation's watermark.
 *
 * Unread is arithmetic on the server — `MessageCount - LastReadCount` — not a per-message flag, so
 * the panel reconstructs *which* messages those are by counting back from the newest. That is the
 * same ordering the server uses to resolve `atMessageId`, which is what keeps the NEW divider and a
 * per-message toggle agreeing with each other.
 *
 * Consequences worth knowing:
 *
 * - Read state is a **boundary**, not a set. There is no way to mark one message in the middle unread
 *   and leave older ones read, and the UI must not imply otherwise — the toggles behave as a slider.
 * - It only makes sense for **one** conversation. With several entities selected the panel is showing
 *   several watermarks merged, and there is no single boundary to draw; callers pass the messages for
 *   the current entity only, and skip the divider entirely when more than one entity is selected.
 */
/**
 * The messages of one audience, oldest first, replies included.
 *
 * **Filtering the tree's roots is not the same thing** and was the bug: the watermark counts every
 * message as it arrived, so a reply belongs to its own audience at its own point in time. Filtering
 * only top-level messages left every reply in whichever lane its parent happened to be, and the
 * client's count then disagreed with the server's by however many replies were involved — which showed
 * up as the message you clicked staying read.
 */
export function flattenAudience(messages: Message[], visibleToClient: boolean): Message[] {
  return flattenChronologically(messages).filter(
    message => message.isVisibleToClient === visibleToClient
  )
}

/** Unread ids within one audience. See {@link flattenAudience} for why this is not a tree filter. */
export function resolveUnreadMessageIdsForAudience(
  messages: Message[],
  unreadCount: number,
  visibleToClient: boolean
): Set<string> {
  if (unreadCount <= 0) return new Set()

  const lane = flattenAudience(messages, visibleToClient)
  return new Set(lane.slice(Math.max(0, lane.length - unreadCount)).map(message => message.id))
}

/** The oldest unread message within one audience — where that tab's NEW divider goes. */
export function resolveFirstUnreadIdForAudience(
  messages: Message[],
  unreadCount: number,
  visibleToClient: boolean
): string | null {
  if (unreadCount <= 0) return null

  const lane = flattenAudience(messages, visibleToClient)
  const index = lane.length - unreadCount
  if (index < 0 || index >= lane.length) return null
  return lane[index].id
}

export function resolveUnreadMessageIds(messages: Message[], unreadCount: number): Set<string> {
  if (unreadCount <= 0 || messages.length === 0) return new Set()

  const chronological = flattenChronologically(messages)
  const firstUnreadIndex = Math.max(0, chronological.length - unreadCount)

  return new Set(chronological.slice(firstUnreadIndex).map(message => message.id))
}

/**
 * Every message in the thread tree, oldest first. Replies are interleaved by their own timestamp
 * rather than kept under their parent, because the watermark counts messages as they arrived — a
 * reply posted this morning is newer than a root message from last week.
 */
export function flattenChronologically(messages: Message[]): Message[] {
  const flat: Message[] = []

  const walk = (list: Message[]) => {
    for (const message of list) {
      flat.push(message)
      if (message.replies?.length) walk(message.replies)
    }
  }
  walk(messages)

  return flat.sort(
    (a, b) => new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime()
  )
}

/**
 * The id of the oldest unread message — where the NEW divider goes. Null when everything is read.
 */
export function resolveFirstUnreadId(messages: Message[], unreadCount: number): string | null {
  if (unreadCount <= 0) return null

  const chronological = flattenChronologically(messages)
  const firstUnreadIndex = chronological.length - unreadCount

  if (firstUnreadIndex < 0 || firstUnreadIndex >= chronological.length) return null
  return chronological[firstUnreadIndex].id
}

/**
 * What the unread count becomes after toggling one message.
 *
 * Mirrors the server's rules exactly, so the optimistic update and the eventual refetch agree:
 * marking read covers that message and everything older, marking unread stops just *before* it, so
 * the message toggled unread is itself unread again.
 */
export function unreadCountAfterToggle(
  messages: Message[],
  messageId: string,
  read: boolean
): number {
  const chronological = flattenChronologically(messages)
  const index = chronological.findIndex(message => message.id === messageId)
  if (index < 0) return 0

  return read
    ? chronological.length - (index + 1)
    : chronological.length - index
}
